import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Button from '@material-ui/core/Button';
import cx from 'classnames';

import withStyles from '~src/lib/isomorphic-style-loader/withStyles';
import commandUrl from '~src/lib/commandUrl';
import isNarvarInternalUser from '~src/lib/isNarvarInternalUser';
import migrationVariant, { MIGRATION_VARIANT } from '~src/lib/migrationVariant';

import s from './MigrationBanner.scss';

/*
 * Hub->Command migration banner (CPMT-5371, CPMT-5481). Driven by the
 * `migration` object hub-backend attaches to each retailer in
 * /api/users/signedin. Not dismissible. When the current retailer is not
 * migrating, renders `children` instead (the default header announcement slot).
 *
 * Still keyed on `is_admin_managed`, so it stays merchant-visible once a
 * retailer goes live in Command — the takeover is gated separately, on
 * `is_hub_access_blocked` (CPMT-5928), and a retailer can be live here while
 * still reaching Hub for a grace period. The Live copy splits by audience
 * (CPMT-5946): merchants get the external grace-period message, Narvar staff
 * the internal one, using the same internal-user check that exempts staff
 * from the takeover.
 */
const MigrationBanner = ({ migration, isInternalUser, children }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const path = window.location.pathname.split('/')[1];
    if (!['admin'].includes(path)) {
      setIsVisible(true);
    }
  }, []);

  const variant = migrationVariant(migration);

  if (!isVisible || variant === null) {
    return children || null;
  }

  if (variant === MIGRATION_VARIANT.LIVE) {
    return (
      <div className={cx(s.banner, s.info)} role="status">
        <div className={s.copyWrap}>
          <span role="img" aria-label="info" className={s.emoji}>
            ℹ️
          </span>
          {isInternalUser ? (
            <p className={s.copy}>
              This account is now on Narvar Command. Only changes made in
              Command are applied to this account&apos;s consumer experiences.
              Please sign in to Command to make changes.
            </p>
          ) : (
            <p className={s.copy}>
              Your account has moved to Narvar Command, the redesigned platform
              for managing your Narvar experience. Only changes made in Command
              are applied to your customer experiences. Your access to Hub will
              end soon.
            </p>
          )}
          <Button
            color="secondary"
            variant="contained"
            href={commandUrl()}
            style={{
              whiteSpace: 'nowrap',
              height: '40px',
              borderRadius: '4px',
              color: '#fff',
            }}
          >
            Go to Command
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cx(s.banner, s.warning)} role="status">
      <div className={s.copyWrap}>
        <span role="img" aria-label="warning" className={s.emoji}>
          ⚠️
        </span>
        <p className={s.copy}>
          Your account is moving to Narvar Command, the redesigned platform for
          managing your Narvar experience. If you need to make updates before
          Tuesday, September 29, 2026, email{' '}
          <a className={s.emailLink} href="mailto:narvarcommand@narvar.com">
            narvarcommand@narvar.com
          </a>{' '}
          so we can ensure your changes carry over.
        </p>
      </div>
    </div>
  );
};

MigrationBanner.propTypes = {
  migration: PropTypes.shape({
    has_started_migration: PropTypes.bool,
    is_admin_managed: PropTypes.bool,
  }),
  isInternalUser: PropTypes.bool,
  children: PropTypes.node,
};

MigrationBanner.defaultProps = {
  migration: null,
  isInternalUser: false,
  children: null,
};

function mapStateToProps(state) {
  const { userReducer: { user } = {} } = state;
  const {
    current_retailer_id: currentRetailerId,
    retailerIdToRetailerInfo: { [currentRetailerId]: retailerInfo } = {},
  } = user || {};

  return {
    migration: (retailerInfo && retailerInfo.migration) || null,
    isInternalUser: isNarvarInternalUser(user),
  };
}

export { MigrationBanner as UnconnectedMigrationBanner };

export default connect(mapStateToProps)(withStyles(s)(MigrationBanner));
